import {
  Capability,
  ComputedRole,
  OrgCapability,
  SectionCapability,
  getComputedRole,
  getCourseCapabilities,
  getOrgCapabilities,
  getPageCapabilities,
  getSectionCapabilities,
  hasOrgCapability,
  hasSectionCapability,
} from "../../shared/capabilities";
import { CourseRole, SectionRole } from "../../shared/types/roles";

import {
  courseUserSelectors,
  createAppSelector,
  createStringArraySelector,
  orgUserSelectors,
  pageSelectors,
  sectionUserSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import {
  selectCourseGroupingUsersForCurrentCourse,
  selectCurrentUserIsCourseGroupingAdminAnywhere,
} from "./course-grouping-user";
import {
  selectCourseRoleByCourseId,
  selectCourseRoleBySectionId,
  selectCourseRoleByUserId,
  selectCurrentCourseRole,
} from "./course-user";
import { selectCurrentOrgRole, selectOrgRoleByUserId } from "./org";
import { selectPageGroupForCurrentUser } from "./page-groups";
import {
  selectCurrentSectionRole,
  selectEnabledSectionUsers,
  selectEnabledSectionUsersForCurrentSection,
  selectSectionRoleBySectionId,
  selectSectionRoleByUserId,
} from "./section-user";
import { selectIsStudentPreview } from "./view";

import type { SectionUserType, UserType } from "components/server-types";
import { compactMap, filterMap } from "utils/collections";
import { getEnabledCourseRole, getEnabledOrgRole, getEnabledSectionRole } from "utils/roles";
import { HomeRole } from "utils/urls";

export const selectUserRoleByUserId = createAppSelector(
  [(state, userId: string) => userId, (state) => state.users],
  (userId, users) => users[userId]?.role ?? null,
);

export const selectCurrentUserRole = createAppSelector(
  [selectCurrentUserId, (state) => state.users],
  (userId, users) => users[userId!]?.role ?? null,
);

// Note: using this selector is discouraged, use capability selectors instead
export const selectCurrentUserComputedRole = createAppSelector(
  [
    selectCurrentUserRole,
    selectCurrentSectionRole,
    selectCurrentOrgRole,
    selectCurrentCourseRole,
    selectIsStudentPreview,
  ],
  (userRole, sectionRole, orgRole, courseRole, isStudentPreview): ComputedRole =>
    getComputedRole({
      userRole,
      sectionRole,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

export const selectCurrentUserOrgRole = createAppSelector(
  [selectCurrentUserRole, selectCurrentOrgRole, selectCurrentCourseRole, selectIsStudentPreview],
  (userRole, orgRole, courseRole, isStudentPreview): ComputedRole =>
    getComputedRole({
      userRole,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

export const selectComputedRoleByUserId = createAppSelector(
  [
    selectUserRoleByUserId,
    selectSectionRoleByUserId,
    selectOrgRoleByUserId,
    selectCourseRoleByUserId,
  ],
  (userRole, sectionRole, orgRole, courseRole): ComputedRole =>
    getComputedRole({
      userRole,
      sectionRole,
      orgRole,
      courseRole,
    }),
);

export const selectCurrentUserComputedRoleBySectionId = createAppSelector(
  [
    selectCurrentUserRole,
    selectSectionRoleBySectionId,
    selectCurrentOrgRole,
    selectCourseRoleBySectionId,
  ],
  (userRole, sectionRole, orgRole, courseRole): ComputedRole =>
    getComputedRole({
      userRole,
      sectionRole,
      orgRole,
      courseRole,
    }),
);

export const selectSectionCapabilitiesBySectionId = createAppSelector(
  [selectCurrentUserComputedRoleBySectionId],
  (computedRole) => getSectionCapabilities(computedRole),
);

export const selectSectionCapabilities = createAppSelector(
  [selectCurrentUserComputedRole],
  (computedRole) => getSectionCapabilities(computedRole),
);

export const selectCourseCapabilities = createAppSelector(
  [selectCurrentUserRole, selectCurrentOrgRole, selectCurrentCourseRole, selectIsStudentPreview],
  (userRole, orgRole, courseRole, isStudentPreview) =>
    getCourseCapabilities(
      getComputedRole({
        userRole,
        orgRole,
        courseRole,
        isStudentPreview,
      }),
    ),
);

export const selectOrgCapabilities = createAppSelector([selectCurrentUserOrgRole], (userOrgRole) =>
  getOrgCapabilities(userOrgRole),
);

export const selectCapabilities = createAppSelector(
  [selectSectionCapabilities, selectCourseCapabilities, selectOrgCapabilities],
  (sectionCapabilities, courseCapabilities, orgCapabilities) => ({
    ...sectionCapabilities,
    ...courseCapabilities,
    ...orgCapabilities,
  }),
);

// The current user's effective role for a specific course. Mirrors the server's
// get_computed_role_in_course.
export const selectCurrentUserComputedRoleByCourseId = createAppSelector(
  [selectCurrentUserRole, selectCurrentOrgRole, selectCourseRoleByCourseId, selectIsStudentPreview],
  (userRole, orgRole, courseRole, isStudentPreview): ComputedRole =>
    getComputedRole({
      userRole,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

// All course-level capabilities for the current user in a specific course.
export const selectCourseCapabilitiesByCourseId = createAppSelector(
  [selectCurrentUserComputedRoleByCourseId],
  (userCourseRole) => getCourseCapabilities(userCourseRole),
);

export const selectPageCapabilities = createAppSelector(
  [pageSelectors.selectById, selectCurrentUserComputedRole, selectPageGroupForCurrentUser],
  (page, computedRole, pageGroup) => getPageCapabilities(page, computedRole, Boolean(pageGroup)),
);

export const selectSectionCapabilitiesByUserId = createAppSelector(
  [selectComputedRoleByUserId],
  (computedRole) => getSectionCapabilities(computedRole),
);

export const selectIsRecordingAgent = createAppSelector(
  [selectCurrentUserComputedRole],
  (computedRole) => computedRole === ComputedRole.RECORDING_AGENT,
);

const getSectionUsersWithSectionCapability = (
  sectionUsers: SectionUserType[],
  capability: SectionCapability,
  sectionId: string | null,
) => {
  if (!sectionId) {
    return [];
  }
  return sectionUsers.filter((sectionUser) => {
    if (sectionUser.section_id !== sectionId) {
      return false;
    }
    const sectionRole = sectionUser.role;
    return hasSectionCapability(
      // sectionRole is guaranteed to be non-null here because it's a section user and it will take
      // precedence over userRole, so we can safely pass null for userRole.
      getComputedRole({
        userRole: null,
        sectionRole,
      }),
      capability,
    );
  });
};

const getSectionUserIdsWithSectionCapability = (
  sectionUsers: SectionUserType[],
  capability: SectionCapability,
  sectionId: string | null,
) => getSectionUsersWithSectionCapability(sectionUsers, capability, sectionId).map(({ id }) => id);

export const selectSectionUserIdsWithSectionCapability = createAppSelector(
  [
    selectEnabledSectionUsers,
    selectCurrentSectionId,
    (state, capability: SectionCapability) => capability,
  ],
  (sectionUsers, sectionId, capability) =>
    getSectionUserIdsWithSectionCapability(sectionUsers, capability, sectionId),
);

// User role map in the current section
// This is used to determine who has what capabilities in the current section.
export const selectComputedRoleMapInCurrentSection = createAppSelector(
  [
    userSelectors.selectAll,
    sectionUserSelectors.filterForCurrentSection,
    orgUserSelectors.selectAll,
    courseUserSelectors.filterForCurrentCourse,
    selectCourseGroupingUsersForCurrentCourse,
  ],
  (users, sectionUsers, orgUsers, courseUsers, groupingUsers): Array<[UserType, ComputedRole]> => {
    // Effective roles only (UNKNOWN dropped): enabled section, course/grouping, capability-bearing
    // org, or platform-wide authority. Disabled SectionUser/OrgUser contribution matches server.

    const sectionUsersByUserId = new Map(
      sectionUsers.map((sectionUser) => [sectionUser.user_id, sectionUser]),
    );

    const orgUsersByUserId = new Map(orgUsers.map((ou) => [ou.user_id, ou]));
    const courseUsersByUserId = new Map(courseUsers.map((cu) => [cu.user_id, cu]));
    const groupingUsersByUserId = new Map(groupingUsers.map((gu) => [gu.user_id, gu]));

    return compactMap(users, (user) => {
      const sectionUser = sectionUsersByUserId.get(user.id) ?? null;
      const courseUser = courseUsersByUserId.get(user.id) ?? null;
      const groupingUser = groupingUsersByUserId.get(user.id) ?? null;
      const orgUser = orgUsersByUserId.get(user.id) ?? null;
      const isOrgUserDisabled = orgUser?.is_disabled ?? false;

      const role = getComputedRole({
        userRole: user.role,
        sectionRole: getEnabledSectionRole(sectionUser, isOrgUserDisabled),
        orgRole: getEnabledOrgRole(orgUser),
        courseRole: getEnabledCourseRole(courseUser ?? groupingUser, isOrgUserDisabled),
      });

      return role !== ComputedRole.UNKNOWN ? ([user, role] as [UserType, ComputedRole]) : null;
    });
  },
);

// User with specific capabilities in the current section

const getUsersWithSectionCapability = (
  computedRoleMap: Array<[UserType, ComputedRole]>,
  capability: SectionCapability,
) => {
  return filterMap(
    computedRoleMap,
    ([, role]) => hasSectionCapability(role, capability),
    ([user]) => user,
  );
};

export const selectUsersWhoCanParticipateInSection = createAppSelector(
  [selectComputedRoleMapInCurrentSection],
  (computedRoleMap) =>
    getUsersWithSectionCapability(computedRoleMap, Capability.CAN_PARTICIPATE_IN_SECTION),
);

// User IDs with specific capabilities in the current section

const getUserIdsWithSectionCapability = (
  computedRoleMap: Array<[UserType, ComputedRole]>,
  capability: SectionCapability,
) => {
  return getUsersWithSectionCapability(computedRoleMap, capability).map((user) => user.id);
};

const createSelectUserIdsWithSectionCapability = (capability: SectionCapability) =>
  createStringArraySelector([selectComputedRoleMapInCurrentSection], (computedRoleMap) =>
    getUserIdsWithSectionCapability(computedRoleMap, capability),
  );

export const selectUserIdsWhoAreVisibleInClass = createSelectUserIdsWithSectionCapability(
  Capability.IS_VISIBLE_IN_CLASS,
);

export const selectUserIdsWhoCanAuthorSection = createSelectUserIdsWithSectionCapability(
  Capability.CAN_AUTHOR_SECTION,
);

export const selectUserIdsWhoCanMentionAllInChat = createSelectUserIdsWithSectionCapability(
  Capability.CAN_MENTION_ALL_IN_CHAT,
);

export const selectUserIdsWhoCanMentionAnyBreakoutGroupInChat =
  createSelectUserIdsWithSectionCapability(Capability.CAN_MENTION_ANY_BREAKOUT_GROUP_IN_CHAT);

export const selectUserIdsWhoCanCreateChatMessage = createSelectUserIdsWithSectionCapability(
  Capability.CAN_CREATE_CHAT_MESSAGE,
);

// STUDENT-only capability; computedRoleMap already omits section/org-disabled enrollments.
export const selectUserIdsWhoCanParticipateInSection = createSelectUserIdsWithSectionCapability(
  Capability.CAN_PARTICIPATE_IN_SECTION,
);

/** Enabled section_user ids for users who can participate in the current section. */
export const selectStudentSectionUserIdsInCurrentSection = createStringArraySelector(
  [selectEnabledSectionUsersForCurrentSection, selectUserIdsWhoCanParticipateInSection],
  (sectionUsers, studentUserIds) => {
    const studentUserIdSet = new Set(studentUserIds);
    return filterMap(
      sectionUsers,
      (sectionUser) => studentUserIdSet.has(sectionUser.user_id),
      (sectionUser) => sectionUser.id,
    );
  },
);

// Home-role availability selectors.
//
// These are intentionally split into atomic boolean selectors so that
// `selectAvailableHomeRoles` only recomputes when the actual rolechanges.
const selectCurrentUserCanAuthorOrg = createAppSelector([selectCurrentUserOrgRole], (orgRole) =>
  hasOrgCapability(orgRole, OrgCapability.CAN_AUTHOR_ORG),
);

export const selectCurrentUserIsCourseAdminInAnyCourse = createAppSelector(
  [courseUserSelectors.filterForCurrentUser, selectCurrentUserIsCourseGroupingAdminAnywhere],
  (courseUsers, isGroupingCourseAdmin) =>
    courseUsers.some((cu) => cu.role === CourseRole.COURSE_ADMIN) || isGroupingCourseAdmin,
);

const selectCurrentUserIsInstructorInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.INSTRUCTOR),
);

const selectCurrentUserIsSectionAssistantInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.SECTION_ASSISTANT),
);

const selectCurrentUserIsStudentInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.STUDENT),
);

export const selectAvailableHomeRoles = createAppSelector(
  [
    selectCurrentUserCanAuthorOrg,
    selectCurrentUserIsCourseAdminInAnyCourse,
    selectCurrentUserIsInstructorInAnySection,
    selectCurrentUserIsSectionAssistantInAnySection,
    selectCurrentUserIsStudentInAnySection,
  ],
  (canAuthorOrg, isCourseAdmin, isInstructor, isSectionAssistant, isStudent): HomeRole[] =>
    [
      (canAuthorOrg || isCourseAdmin) && HomeRole.ADMIN,
      isInstructor && HomeRole.INSTRUCTOR,
      isSectionAssistant && HomeRole.SECTION_ASSISTANT,
      isStudent && HomeRole.STUDENT,
    ].filter(Boolean) as HomeRole[],
);
